import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CharterClauseDto } from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { CharterProvenanceComponent } from '@features/dashboard/company/corporate-charter/components/charter-provenance/charter-provenance.component';
import { dominantSource } from '@features/dashboard/company/corporate-charter/corporate-charter-provenance.util';
import {
  CLAUSE_CATEGORY_LABEL,
  CREDIT_RELEVANCE_ORDER,
  formatPercent,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';

/**
 * "Cláusulas que importam para crédito": restrições a garantia, oneração e dívida, quórum, cessão, preferência...
 * Ordenadas pela relevância para crédito que o agente marcou (alta primeiro, com o ponto âmbar).
 */
@Component({
  selector: 'app-charter-clauses-card',
  imports: [CharterProvenanceComponent],
  template: `
    <header class="card-head">
      <h2 class="card-title">Cláusulas que importam para crédito</h2>
      @if (source(); as s) {
        <app-charter-provenance [from]="s" variant="source" [current]="true" />
      }
    </header>
    @if (sorted().length === 0) {
      <p class="empty">Nenhuma cláusula relevante para crédito nos atos lidos.</p>
    } @else {
      <ul class="clauses">
        @for (clause of sorted(); track $index) {
          <li class="clause prov-row">
            <span
              class="clause__dot"
              [class.clause__dot--high]="clause.creditRelevance === 'high'"
              [class.clause__dot--medium]="clause.creditRelevance === 'medium'"
              [attr.aria-label]="'Relevância ' + relevance[clause.creditRelevance]"></span>
            <div class="clause__body">
              <span class="clause__summary">{{ clause.summary }}</span>
              <span class="clause__meta">
                {{ category[clause.category] }}
                @if (clause.quorumPercent !== null && clause.quorumPercent !== undefined) {
                  · quórum {{ formatPercent(clause.quorumPercent) }}
                }
                @if (clause.clauseRef) {
                  · {{ clause.clauseRef }}
                }
                <app-charter-provenance
                  [from]="clause"
                  [current]="true"
                  variant="icon"
                  [base]="source()" />
              </span>
            </div>
          </li>
        }
      </ul>
    }
  `,
  styleUrl: './charter-clauses-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterClausesCardComponent {
  readonly clauses = input<CharterClauseDto[]>([]);

  protected readonly category = CLAUSE_CATEGORY_LABEL;
  protected readonly formatPercent = formatPercent;
  protected readonly relevance = { high: 'alta', medium: 'média', low: 'baixa' } as const;

  /** Fonte dominante: o ato de onde vem a maioria das cláusulas; as de outro ato ganham o selo. */
  protected readonly source = computed(() => dominantSource(this.clauses()));

  protected readonly sorted = computed(() =>
    [...this.clauses()].sort(
      (a, b) =>
        CREDIT_RELEVANCE_ORDER[a.creditRelevance] - CREDIT_RELEVANCE_ORDER[b.creditRelevance]
    )
  );
}
